import * as constants from '@/constants';
import i18n from '@/settings/i18n';
import { useFlashMessenger } from './useFlashMessenger';
import { useCallback } from 'react';
import { stringifyUrl } from 'query-string';

import { pages } from '@/settings/pages';
import { FLASH_MESSAGE_LIFETIME_MILLISEC } from '@/constants';
import { FROM_PATH_KEY } from '@/constants';
import { sentryReportError } from '@/utils/sentryReportError';

export const useApiErrorHandler = () => {
  const { flashError } = useFlashMessenger();

  const redirectToReAuthentication = useCallback(
    (gloplaId?: string, fromPath?: string) => {
      const queryParams = {
        prompt: 'login',
        ...(fromPath && { [FROM_PATH_KEY]: fromPath }),
        ...(gloplaId && { glopla_id: gloplaId }),
      };
      const signinUri = stringifyUrl({
        url: pages.signin.index,
        query: queryParams,
      });
      location.assign(signinUri);
    },
    [],
  );

  const apiErrorHandler = useCallback(
    (
      err: any,
      gloplaId?: string | undefined,
      fromPath?: string | undefined,
    ) => {
      if (
        err.response &&
        err.response.status === 401 &&
        err.response.data &&
        err.response.data.error === constants.INVALID_ACCESS_TOKEN_ERROR
      ) {
        flashError(i18n.t('ui.error.unexpected_and_login'));
        return;
      }

      if (
        err.response &&
        err.response.data &&
        err.response.data.error === constants.REAUTHENTICATION_EXPIRED_ERROR
      ) {
        flashError(i18n.t('ui.error.reauthentication_expired'));
        setTimeout(() => {
          redirectToReAuthentication(gloplaId, fromPath);
        }, FLASH_MESSAGE_LIFETIME_MILLISEC);
        return;
      }

      if (
        err.response &&
        err.response.status === 409 &&
        err.response.data &&
        err.response.data.error === constants.ADMIN_ALREADY_DELETED_ERROR
      ) {
        flashError(i18n.t('ui.error.admin_already_deleted'));
        return;
      }

      // MEMO: 503エラーはインターセプターでメンテナンスメッセージを表示済みのためスキップ
      // src/services/api/axios/interceptors.ts
      if (err.response && err.response.status === 503) {
        return;
      }

      sentryReportError(err);

      flashError(i18n.t('ui.error.unexpected'));
    },
    [flashError, redirectToReAuthentication],
  );

  return { apiErrorHandler };
};
